import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './App.css';
import GigForm from './components/GigForm';
import GigDetails from './components/GigDetails';
import ExpenseForm from './components/ExpenseForm';
import AdditionalChargeForm from './components/AdditionalChargeForm';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import UserProfile from './components/UserProfile';
import Tickets from './components/Tickets';
import ClientsPage from './components/ClientsPage';
import LocationsPage from './components/LocationsPage';
import GigsList from './components/GigsList';
import Modal from './components/Modal';
import Roadmap from './components/Roadmap';
import notificationService from './utils/notifications';
import gigNotificationChecker from './utils/gigNotificationChecker';

const localizer = momentLocalizer(moment);
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Configure axios to send credentials with all requests
axios.defaults.withCredentials = true;

// Create a ref to store the logout handler so we can use it in the interceptor
let globalLogoutHandler = null;
let globalMaintenanceHandler = null;

// Axios response interceptor to handle authentication errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 503 (Service Unavailable) - Maintenance Mode
    if (error.response && error.response.status === 503 && error.response.data?.maintenance) {
      if (globalMaintenanceHandler) {
        globalMaintenanceHandler(error.response.data.message);
      }
      return Promise.reject(error);
    }
    
    // Handle 401 (Unauthorized) and 403 (Forbidden) errors
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Only call logout if we have a handler set (prevents infinite loops)
      if (globalLogoutHandler && !error.config._retry) {
        error.config._retry = true;
        globalLogoutHandler();
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [gigs, setGigs] = useState([]);
  const [selectedGig, setSelectedGig] = useState(null);
  const [showGigForm, setShowGigForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showAdditionalChargeForm, setShowAdditionalChargeForm] = useState(false);
  const [editingGig, setEditingGig] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingAdditionalCharge, setEditingAdditionalCharge] = useState(null);
  const [initialGigDate, setInitialGigDate] = useState(null);
  const [view, setView] = useState('month');
  const [calendarHeight, setCalendarHeight] = useState(600);
  const [currentPage, setCurrentPage] = useState('dashboard'); // 'dashboard', 'gigs', 'clients', 'locations'
  const [gigsViewMode, setGigsViewMode] = useState('list'); // 'calendar' or 'list'
  const [gigsInitialFilters, setGigsInitialFilters] = useState(null); // Initial filters for GigsList
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Reset filters when navigating away from gigs page
  useEffect(() => {
    if (currentPage !== 'gigs' && gigsInitialFilters) {
      setGigsInitialFilters(null);
    }
  }, [currentPage, gigsInitialFilters]);
  const [_0xa1b2, set_0xa1b2] = useState(false);
  const [_0xc3d4, set_0xc3d4] = useState(null);
  const profileLinkTapRef = useRef({ lastTap: 0, timer: null });
  const [footerText, setFooterText] = useState('');
  const [appVersion, setAppVersion] = useState('1.0.1');
  const [serverStatus, setServerStatus] = useState('checking'); // 'online', 'offline', 'checking'

  const fetchGigs = async (updatedGig = null) => {
    try {
      if (updatedGig) {
        console.log('fetchGigs called with updatedGig:', updatedGig);
        console.log('updatedGig.paid_amount:', updatedGig.paid_amount);
        // If an updated gig is provided, update the gigs list and selectedGig directly
        // Preserve expenses from the existing gig if the updated gig doesn't have them
        setGigs(prevGigs => {
          const updatedGigs = prevGigs.map(g => {
            if (g.id === updatedGig.id) {
              // Preserve expenses and additional_charges if they exist in the current gig but not in the updated gig
              let finalGig = updatedGig;
              if (g.expenses && (!updatedGig.expenses || updatedGig.expenses.length === 0)) {
                finalGig = { ...finalGig, expenses: g.expenses };
              }
              if (g.additional_charges && (!updatedGig.additional_charges || updatedGig.additional_charges.length === 0)) {
                finalGig = { ...finalGig, additional_charges: g.additional_charges };
              }
              console.log('Updating gig in list, finalGig.paid_amount:', finalGig.paid_amount);
              return finalGig;
            }
            return g;
          });
          return updatedGigs;
        });
        if (selectedGig && selectedGig.id === updatedGig.id) {
          // Preserve expenses and additional_charges when updating selectedGig
          let gigWithData = updatedGig;
          if (selectedGig.expenses && (!updatedGig.expenses || updatedGig.expenses.length === 0)) {
            gigWithData = { ...gigWithData, expenses: selectedGig.expenses };
          }
          if (selectedGig.additional_charges && (!updatedGig.additional_charges || updatedGig.additional_charges.length === 0)) {
            gigWithData = { ...gigWithData, additional_charges: selectedGig.additional_charges };
          }
          console.log('Updating selectedGig, gigWithData.paid_amount:', gigWithData.paid_amount);
          setSelectedGig(gigWithData);
        }
        return [updatedGig];
      } else {
        // Otherwise, fetch all gigs
        const response = await axios.get(`${API_BASE}/gigs`);
        setGigs(response.data);
        // Update selectedGig if it exists
        if (selectedGig) {
          const updatedGig = response.data.find(g => g.id === selectedGig.id);
          if (updatedGig) {
            setSelectedGig(updatedGig);
          }
        }
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching gigs:', error);
      // Don't manually set user to null here - the axios interceptor will handle it
      // This prevents race conditions and duplicate logout calls
      return [];
    }
  };

  // Check if user is authenticated on mount
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/me`);
      const userData = response.data;
      console.log('User data from auth/me:', userData); // Debug log
      console.log('User role:', userData.role); // Debug log
      setUser(userData);
      // Fetch gigs after user is set
      try {
        const responseGigs = await axios.get(`${API_BASE}/gigs`);
        setGigs(responseGigs.data);
      } catch (gigError) {
        console.error('Error fetching gigs:', gigError);
        // If gigs fetch fails with 401/403, the interceptor will handle logout
        if (gigError.response?.status === 401 || gigError.response?.status === 403) {
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Don't show alert for 401 (session expired) - just log out silently
      if (error.response?.status === 403) {
        alert('Your account has been disabled. Please contact an administrator.');
      }
      // Always set user to null on auth failure
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (userData) => {
    setUser(userData);
    await fetchGigs();
  };

  const handleRegister = async (userData) => {
    setUser(userData);
    await fetchGigs();
  };

  const logoutHandlerRef = useRef(null);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if the server request fails (e.g., session already expired)
    } finally {
      setUser(null);
      setGigs([]);
      setSelectedGig(null);
      setCurrentPage('dashboard');
    }
  }, []);

  // Update the ref whenever handleLogout changes
  logoutHandlerRef.current = handleLogout;

  // Set the global logout handler for the axios interceptor
  useEffect(() => {
    globalLogoutHandler = () => {
      if (logoutHandlerRef.current) {
        logoutHandlerRef.current();
      }
    };
    return () => {
      globalLogoutHandler = null;
    };
  }, []);

  // Set the global maintenance handler for the axios interceptor
  useEffect(() => {
    globalMaintenanceHandler = (message) => {
      // Don't kick joliveira during maintenance
      const isSystemAdmin = user && user.username === atob('am9saXZlaXJh');
      if (!isSystemAdmin) {
        setMaintenanceMode(true);
        setMaintenanceMessage(message || 'System is under maintenance. Please try again later.');
        setUser(null);
      }
    };
    return () => {
      globalMaintenanceHandler = null;
    };
  }, [user]);

  // Check maintenance status periodically when in maintenance mode
  useEffect(() => {
    if (!maintenanceMode) return;
    
    const checkMaintenance = async () => {
      try {
        const response = await axios.get(`${API_BASE}/maintenance/status`);
        if (!response.data.maintenance) {
          setMaintenanceMode(false);
          setMaintenanceMessage('');
          // Re-check auth
          checkAuth();
        }
      } catch (error) {
        // Still in maintenance or server down
      }
    };
    
    const interval = setInterval(checkMaintenance, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [maintenanceMode]);

  useEffect(() => {
    const updateHeight = () => {
      // Increase mobile calendar height for better visibility
      setCalendarHeight(window.innerWidth < 768 ? 600 : 600);
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Check URL for reset token on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResetToken(token);
    }
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
    fetchFooterText();
    checkServerStatus();
    
    // Request notification permission by default (if not already set)
    if ('Notification' in window && Notification.permission === 'default') {
      notificationService.requestPermission().catch(error => {
        console.log('Notification permission request:', error);
      });
    }
    
    // Check server status every 30 seconds
    const statusInterval = setInterval(checkServerStatus, 30000);
    return () => clearInterval(statusInterval);
  }, []);

  // Check for gig notifications periodically (minimized - only once per day)
  useEffect(() => {
    if (!user || gigs.length === 0) return;

    // Check once when gigs are loaded (but only shows if conditions are met)
    gigNotificationChecker.check(gigs);
    gigNotificationChecker.cleanup(gigs);

    // Check once per day (every 24 hours) - minimized frequency
    const notificationInterval = setInterval(() => {
      gigNotificationChecker.check(gigs);
      gigNotificationChecker.cleanup(gigs);
    }, 24 * 60 * 60 * 1000); // 24 hours

    // Also check when window regains focus (but only if it's a new day)
    const handleFocus = () => {
      gigNotificationChecker.check(gigs);
      gigNotificationChecker.cleanup(gigs);
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(notificationInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, gigs]);

  const fetchFooterText = async () => {
    try {
      const response = await axios.get('/api/settings/footer');
      setFooterText(response.data.footer_text || '');
      setAppVersion(response.data.version || '1.0.1');
    } catch (error) {
      console.error('Error fetching footer text:', error);
    }
  };

  const checkServerStatus = async () => {
    try {
      const response = await axios.get('/api/health', { timeout: 5000 });
      if (response.data && response.data.status === 'ok') {
        setServerStatus('online');
      } else {
        setServerStatus('offline');
      }
    } catch (error) {
      setServerStatus('offline');
    }
  };

  useEffect(() => {
    const _0xe5f6 = (e) => {
      const _0x7g8h = user && user.username === atob('am9saXZlaXJh');
      if (_0x7g8h && e.key === 'Escape' && _0xa1b2) {
        set_0xa1b2(false);
        setCurrentPage('dashboard');
      }
    };
    window.addEventListener('keydown', _0xe5f6);
    return () => window.removeEventListener('keydown', _0xe5f6);
  }, [user, _0xa1b2]);


  const handleSelectEvent = (event) => {
    const gig = gigs.find(g => g.id === event.resource);
    setSelectedGig(gig);
    setShowGigForm(false);
    setShowExpenseForm(false);
    setShowAdditionalChargeForm(false);
    setSidebarOpen(true);
  };

  const handleSelectGigFromList = (gig) => {
    setSelectedGig(gig);
    setShowGigForm(false);
    setShowExpenseForm(false);
    setShowAdditionalChargeForm(false);
    setSidebarOpen(true);
    setGigsViewMode('calendar'); // Switch to calendar view to show details
  };

  const handleSelectSlot = ({ start }) => {
    setEditingGig(null);
    setSelectedGig(null);
    setInitialGigDate(moment(start).format('YYYY-MM-DD'));
    setShowGigForm(true);
    setShowExpenseForm(false);
  };

  const handleGigSave = async (gigData) => {
    try {
      if (editingGig) {
        await axios.put(`${API_BASE}/gigs/${editingGig.id}`, gigData);
        notificationService.showSuccess('Gig updated successfully!');
      } else {
        await axios.post(`${API_BASE}/gigs`, gigData);
        notificationService.showSuccess('Gig created successfully!');
      }
      await fetchGigs();
      // Reset notification checker when gigs are updated
      gigNotificationChecker.reset();
      setShowGigForm(false);
      setEditingGig(null);
      setSelectedGig(null);
    } catch (error) {
      console.error('Error saving gig:', error);
      notificationService.showError('Error saving gig. Please try again.');
      alert('Error saving gig. Please try again.');
    }
  };

  const handleGigDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gig?')) {
      try {
        await axios.delete(`${API_BASE}/gigs/${id}`);
        await fetchGigs();
        gigNotificationChecker.reset();
        setSelectedGig(null);
        setShowGigForm(false);
      } catch (error) {
        console.error('Error deleting gig:', error);
        alert('Error deleting gig. Please try again.');
      }
    }
  };

  const handleExpenseSave = async (expenseData) => {
    try {
      if (expenseData.id) {
        // Update existing expense
        await axios.put(`${API_BASE}/expenses/${expenseData.id}`, {
          description: expenseData.description,
          amount: expenseData.amount,
          expense_date: expenseData.expense_date,
          covered_by: expenseData.covered_by || 'self'
        });
      } else {
        // Create new expense
        await axios.post(`${API_BASE}/expenses`, expenseData);
      }
      const updatedGigs = await fetchGigs();
      
      // Update selectedGig if one is selected
      if (selectedGig) {
        const updatedGig = updatedGigs.find(g => g.id === selectedGig.id);
        if (updatedGig) {
          setSelectedGig(updatedGig);
        }
      }
      
      setShowExpenseForm(false);
      setEditingExpense(null);
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Error saving expense. Please try again.');
    }
  };

  const handleExpenseDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await axios.delete(`${API_BASE}/expenses/${id}`);
        const updatedGigs = await fetchGigs();
        
        // Update selectedGig if one is selected
        if (selectedGig) {
          const updatedGig = updatedGigs.find(g => g.id === selectedGig.id);
          if (updatedGig) {
            setSelectedGig(updatedGig);
          }
        }
      } catch (error) {
        console.error('Error deleting expense:', error);
        alert('Error deleting expense. Please try again.');
      }
    }
  };

  const handleEditGig = (gig) => {
    setEditingGig(gig);
    setSelectedGig(null);
    setInitialGigDate(null); // Clear initial date when editing existing gig
    setShowGigForm(true);
    setShowExpenseForm(false);
  };

  const handleAddExpense = () => {
    setEditingExpense(null);
    setShowExpenseForm(true);
    setShowGigForm(false);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setShowExpenseForm(true);
    setShowGigForm(false);
  };

  const handleAdditionalChargeSave = async (chargeData) => {
    try {
      let response;
      if (chargeData.id) {
        // Update existing charge
        response = await axios.put(`${API_BASE}/additional-charges/${chargeData.id}`, {
          charge_type: chargeData.charge_type,
          amount: chargeData.amount,
          notes: chargeData.notes || null,
          substitute_name: chargeData.substitute_name || null
        });
      } else {
        // Create new charge - backend returns the full gig
        response = await axios.post(`${API_BASE}/additional-charges`, chargeData);
      }
      
      // Backend returns the full gig, use it to update the state
      const updatedGig = response.data;
      await fetchGigs(updatedGig);
      
      setShowAdditionalChargeForm(false);
      setEditingAdditionalCharge(null);
    } catch (error) {
      console.error('Error saving additional charge:', error);
      alert(error.response?.data?.error || 'Error saving additional charge. Please try again.');
    }
  };

  const handleAdditionalChargeDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this additional charge?')) {
      try {
        await axios.delete(`${API_BASE}/additional-charges/${id}`);
        const updatedGigs = await fetchGigs();
        
        // Update selectedGig if one is selected
        if (selectedGig) {
          const updatedGig = updatedGigs.find(g => g.id === selectedGig.id);
          if (updatedGig) {
            setSelectedGig(updatedGig);
          }
        }
      } catch (error) {
        console.error('Error deleting additional charge:', error);
        alert('Error deleting additional charge. Please try again.');
      }
    }
  };

  const handleAddAdditionalCharge = () => {
    setEditingAdditionalCharge(null);
    setShowAdditionalChargeForm(true);
    setShowGigForm(false);
  };

  const handleEditAdditionalCharge = (charge) => {
    setEditingAdditionalCharge(charge);
    setShowAdditionalChargeForm(true);
    setShowGigForm(false);
  };

  const today = moment().startOf('day');
  const calendarEvents = gigs.map(gig => {
    const start = new Date(gig.start_date);
    const end = new Date(gig.end_date);
    end.setHours(23, 59, 59, 999); // Set to end of day
    const isUpcoming = gig.start_date && moment(gig.start_date).startOf('day').isAfter(today);
    
    return {
      title: gig.title,
      start: start,
      end: end,
      resource: gig.id,
      allDay: true,
      is_canceled: gig.is_canceled === 1 || gig.is_canceled === true,
      is_upcoming: isUpcoming
    };
  });

  // Style function for calendar events - highlight canceled gigs in red, upcoming gigs with lighter yellow
  const eventStyleGetter = (event) => {
    if (event.is_canceled) {
      return {
        className: 'rbc-event-canceled',
        style: {
          backgroundColor: '#dc3545',
          color: '#fff',
          border: '2px solid #c82333',
          borderRadius: '4px'
        }
      };
    }
    if (event.is_upcoming) {
      return {
        className: 'rbc-event-upcoming',
        style: {
          backgroundColor: '#fff9c4',
          color: '#1a1a1a',
          border: '1px solid #ffd700',
          borderRadius: '4px'
        }
      };
    }
    return {
      className: 'rbc-event-past',
      style: {
        backgroundColor: '#ffd700',
        color: '#1a1a1a',
        border: 'none',
        borderRadius: '4px'
      }
    };
  };

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="app">
        <div className="loading-screen">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show maintenance mode screen (but allow joliveira through, and allow login form to show)
  const isSystemAdmin = user && user.username === atob('am9saXZlaXJh');
  // Only show maintenance screen if user is logged in but NOT joliveira
  // If not logged in (user is null), let them see the login form so joliveira can log in
  if (maintenanceMode && user && !isSystemAdmin) {
    return (
      <div className="app">
        <div className="maintenance-screen">
          <div className="maintenance-content">
            <div className="maintenance-icon">🔧</div>
            <h1>System Maintenance</h1>
            <p className="maintenance-message">{maintenanceMessage}</p>
            <div className="maintenance-spinner">
              <div className="spinner"></div>
            </div>
            <p className="maintenance-subtitle">
              We're performing scheduled maintenance.<br />
              The page will automatically refresh when the system is back online.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show login/register/forgot password/reset password if not authenticated
  if (!user) {
    if (resetToken) {
      return (
        <ResetPassword 
          token={resetToken}
          onSuccess={() => {
            setResetToken(null);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }}
        />
      );
    }

    if (showForgotPassword) {
      return (
        <ForgotPassword 
          onBack={() => setShowForgotPassword(false)}
        />
      );
    }
    
    return showRegister ? (
      <Register 
        onRegister={handleRegister}
        onSwitchToLogin={() => setShowRegister(false)}
      />
    ) : (
      <Login 
        onLogin={handleLogin}
        onSwitchToRegister={() => setShowRegister(true)}
        onForgotPassword={() => setShowForgotPassword(true)}
      />
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1 1 auto', minWidth: 0 }}>
          <h1>Esfincter</h1>
          {currentPage === 'gigs' && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'nowrap', marginLeft: '0.5rem' }}>
              <div className="view-toggle">
                <button
                  className={`view-toggle-btn ${gigsViewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => setGigsViewMode('calendar')}
                >
                  📅 Calendar
                </button>
                <button
                  className={`view-toggle-btn ${gigsViewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setGigsViewMode('list')}
                >
                  📋 List
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap', flexShrink: 0 }}>
          <button 
            className="btn btn-secondary"
            onClick={() => { setCurrentPage('tickets'); set_0xa1b2(false); }}
            style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
            title="Support Tickets"
          >
            🎫 Tickets
          </button>
          <span 
            className="profile-link"
            onClick={(e) => {
              const _0x9i0j = user && user.username === atob('am9saXZlaXJh');
              const now = Date.now();
              const timeSinceLastTap = now - profileLinkTapRef.current.lastTap;
              
              // Clear any pending single-click timer
              if (profileLinkTapRef.current.timer) {
                clearTimeout(profileLinkTapRef.current.timer);
                profileLinkTapRef.current.timer = null;
              }
              
              // Check for double-tap (within 400ms)
              if (_0x9i0j && timeSinceLastTap < 400 && timeSinceLastTap > 0) {
                // Double tap detected - open sudo panel
                e.preventDefault();
                e.stopPropagation();
                profileLinkTapRef.current.lastTap = 0; // Reset
                if (!_0xc3d4) {
                  import('./components/_SystemMgmt').then(mod => {
                    set_0xc3d4(mod);
                    set_0xa1b2(true);
                    setCurrentPage('dashboard');
                  });
                } else {
                  set_0xa1b2(!_0xa1b2);
                }
              } else {
                // Single tap - wait a bit to see if there's a second tap
                profileLinkTapRef.current.lastTap = now;
                profileLinkTapRef.current.timer = setTimeout(() => {
                  // No second tap, proceed with single-click action
                  setCurrentPage('profile');
                  set_0xa1b2(false);
                  profileLinkTapRef.current.lastTap = 0;
                  profileLinkTapRef.current.timer = null;
                }, 400);
              }
            }}
            onDoubleClick={(e) => {
              const _0x9i0j = user && user.username === atob('am9saXZlaXJh');
              if (_0x9i0j) {
                e.preventDefault();
                e.stopPropagation();
                // Clear single-click timer
                if (profileLinkTapRef.current.timer) {
                  clearTimeout(profileLinkTapRef.current.timer);
                  profileLinkTapRef.current.timer = null;
                }
                profileLinkTapRef.current.lastTap = 0;
                if (!_0xc3d4) {
                  import('./components/_SystemMgmt').then(mod => {
                    set_0xc3d4(mod);
                    set_0xa1b2(true);
                    setCurrentPage('dashboard');
                  });
                } else {
                  set_0xa1b2(!_0xa1b2);
                }
              }
            }}
            title="Click to view your profile, double-click/tap for sudo panel"
          >
            Welcome, {user.username}
          </span>
          {user && (user.role === 'admin' || user.role === 'Admin') && (
            <button 
              className="btn" 
              onClick={() => { setCurrentPage('admin'); set_0xa1b2(false); }}
              title="Admin Dashboard"
              style={{ 
                padding: '0.5rem', 
                fontSize: '1.2rem',
                background: '#ffd700',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                minWidth: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ⚙️
            </button>
          )}
          {user && user.username === atob('am9saXZlaXJh') && (
            <button 
              className="btn" 
              onClick={() => {
                if (!_0xc3d4) {
                  import('./components/_SystemMgmt').then(mod => {
                    set_0xc3d4(mod);
                    set_0xa1b2(true);
                    setCurrentPage('dashboard');
                  });
                } else {
                  set_0xa1b2(true);
                  setCurrentPage('dashboard');
                }
              }}
              title="System Management"
              style={{ 
                padding: '0.5rem', 
                fontSize: '1.2rem',
                background: _0xa1b2 ? '#ff6b6b' : '#4a4a4a',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                minWidth: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              🔧
            </button>
          )}
          <button 
            className="btn btn-secondary" 
            onClick={handleLogout}
            title="Logout"
            style={{ 
              padding: '0.5rem', 
              fontSize: '1.2rem',
              minWidth: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            🚪➡️
          </button>
        </div>
      </header>

      <div className="app-body">
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <aside className="sidebar-nav" style={{ display: currentPage === 'admin' ? 'none' : 'block' }}>
            <nav className="side-nav">
              <button
                className={`nav-item ${currentPage === 'dashboard' ? 'active' : ''}`}
                onClick={() => { setCurrentPage('dashboard'); set_0xa1b2(false); }}
              >
                <span className="nav-icon">📊</span>
                <span className="nav-label">Dashboard</span>
              </button>
              <button
                className={`nav-item ${currentPage === 'gigs' ? 'active' : ''}`}
                onClick={() => { setCurrentPage('gigs'); set_0xa1b2(false); }}
              >
                <span className="nav-icon">📅</span>
                <span className="nav-label">Gigs</span>
              </button>
              <button
                className={`nav-item ${currentPage === 'clients' ? 'active' : ''}`}
                onClick={() => { setCurrentPage('clients'); set_0xa1b2(false); }}
              >
                <span className="nav-icon">👥</span>
                <span className="nav-label">Clients</span>
              </button>
              <button
                className={`nav-item ${currentPage === 'locations' ? 'active' : ''}`}
                onClick={() => { setCurrentPage('locations'); set_0xa1b2(false); }}
              >
                <span className="nav-icon">📍</span>
                <span className="nav-label">Venues</span>
              </button>
              <button
                className={`nav-item ${currentPage === 'roadmap' ? 'active' : ''}`}
                onClick={() => { setCurrentPage('roadmap'); set_0xa1b2(false); }}
              >
                <span className="nav-icon">🗺️</span>
                <span className="nav-label">News</span>
              </button>
            </nav>
          </aside>

          <main className={`app-main ${currentPage === 'gigs' ? 'gigs-layout' : ''} ${currentPage === 'admin' ? 'admin-layout' : ''} ${currentPage === 'gigs' && selectedGig && sidebarOpen ? 'gigs-layout-with-sidebar' : ''}`}>
        {_0xa1b2 && _0xc3d4 && user && user.username === atob('am9saXZlaXJh') ? (
          <_0xc3d4.default user={user} onClose={() => { set_0xa1b2(false); setCurrentPage('dashboard'); }} onFooterUpdate={fetchFooterText} />
        ) : currentPage === 'dashboard' ? (
          <Dashboard 
            gigs={gigs}
            user={user}
            onNewGig={() => {
              setEditingGig(null);
              setSelectedGig(null);
              setInitialGigDate(null);
              setShowGigForm(true);
              setShowExpenseForm(false);
              setCurrentPage('gigs');
            }}
            onSelectGig={handleSelectGigFromList}
            onEditGig={handleEditGig}
            onDeleteGig={handleGigDelete}
            onNavigateToGigs={(filters) => {
              setGigsInitialFilters(filters || null);
              setGigsViewMode('list'); // Ensure we're in list view when navigating from dashboard
              setCurrentPage('gigs');
            }}
            onNavigateToProfile={() => setCurrentPage('profile')}
          />
        ) : currentPage === 'admin' ? (
          (() => {
            console.log('App.js: Rendering AdminDashboard, user:', user);
            console.log('App.js: _0xa1b2 state:', _0xa1b2);
            return <AdminDashboard user={user} onNavigate={setCurrentPage} />;
          })()
        ) : currentPage === 'profile' ? (
          <UserProfile user={user} onUserUpdate={(updatedUser) => setUser(updatedUser)} />
        ) : currentPage === 'tickets' ? (
          <Tickets />
        ) : currentPage === 'clients' ? (
          <ClientsPage />
        ) : currentPage === 'locations' ? (
          <LocationsPage />
        ) : currentPage === 'roadmap' ? (
          <Roadmap />
        ) : (
          <>
            {/* Mobile view toggle - shown above content on mobile */}
            {currentPage === 'gigs' && (
              <div className="mobile-view-toggle">
                <div className="view-toggle">
                  <button
                    className={`view-toggle-btn ${gigsViewMode === 'calendar' ? 'active' : ''}`}
                    onClick={() => setGigsViewMode('calendar')}
                  >
                    📅 Calendar
                  </button>
                  <button
                    className={`view-toggle-btn ${gigsViewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setGigsViewMode('list')}
                  >
                    📋 List
                  </button>
                </div>
              </div>
            )}
            {gigsViewMode === 'calendar' ? (
              <>
                <div className={`calendar-container ${selectedGig && sidebarOpen ? 'with-sidebar' : 'full-width'}`}>
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: calendarHeight }}
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={(event) => {
                      handleSelectEvent(event);
                      setSidebarOpen(true);
                    }}
                    onSelectSlot={handleSelectSlot}
                    selectable
                    longPressThreshold={10}
                    view={view}
                    onView={setView}
                    views={['month', 'week', 'day', 'agenda']}
                  />
                </div>

                {selectedGig && sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
                <div className={`sidebar ${selectedGig && sidebarOpen ? 'open' : 'closed'}`}>
                  {selectedGig ? (
                    <>
                      <button 
                        className="sidebar-close"
                        onClick={() => setSidebarOpen(false)}
                        aria-label="Close sidebar"
                      >
                        ×
                      </button>
                      <GigDetails
                        gig={selectedGig}
                        onEdit={handleEditGig}
                        onDelete={handleGigDelete}
                        onAddExpense={handleAddExpense}
                        onEditExpense={handleEditExpense}
                        onExpenseDelete={handleExpenseDelete}
                        onAddAdditionalCharge={handleAddAdditionalCharge}
                        onEditAdditionalCharge={handleEditAdditionalCharge}
                        onAdditionalChargeDelete={handleAdditionalChargeDelete}
                        onGigUpdate={fetchGigs}
                      />
                    </>
                  ) : (
                    <div className="empty-state">
                      <p>Select a gig from the calendar or create a new one to get started.</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <GigsList
                  gigs={gigs}
                  selectedGig={selectedGig}
                  user={user}
                  onSelectGig={(gig) => {
                    setSelectedGig(gig);
                    setSidebarOpen(true); // Show sidebar with details, don't switch to calendar
                  }}
                  onEditGig={handleEditGig}
                  onDeleteGig={handleGigDelete}
                  onNewGig={() => {
                    setEditingGig(null);
                    setSelectedGig(null);
                    setInitialGigDate(null);
                    setShowGigForm(true);
                    setShowExpenseForm(false);
                  }}
                  initialFilters={gigsInitialFilters}
                />
                {/* Show gig details sidebar when a gig is selected from list view */}
                {selectedGig && sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
                <div className={`sidebar ${selectedGig && sidebarOpen ? 'open' : 'closed'}`}>
                  {selectedGig ? (
                    <>
                      <button 
                        className="sidebar-close"
                        onClick={() => {
                          setSidebarOpen(false);
                          setSelectedGig(null);
                        }}
                        aria-label="Close sidebar"
                      >
                        ×
                      </button>
                      <GigDetails
                        gig={selectedGig}
                        onEdit={handleEditGig}
                        onDelete={handleGigDelete}
                        onAddExpense={handleAddExpense}
                        onEditExpense={handleEditExpense}
                        onExpenseDelete={handleExpenseDelete}
                        onAddAdditionalCharge={handleAddAdditionalCharge}
                        onEditAdditionalCharge={handleEditAdditionalCharge}
                        onAdditionalChargeDelete={handleAdditionalChargeDelete}
                        onGigUpdate={fetchGigs}
                      />
                    </>
                  ) : (
                    <div className="empty-state">
                      <p>Select a gig from the list to view details.</p>
                    </div>
                  )}
                </div>
              </>
            )}

        {/* Modal for Gig Form */}
        <Modal
          isOpen={showGigForm}
          onClose={() => {
            setShowGigForm(false);
            setEditingGig(null);
          }}
          title={editingGig ? 'Edit Gig' : 'New Gig'}
        >
          <GigForm
            gig={editingGig}
            initialDate={initialGigDate}
            onSave={(gigData) => {
              handleGigSave(gigData);
              setShowGigForm(false);
              setEditingGig(null);
              setInitialGigDate(null);
            }}
            onCancel={() => {
              setShowGigForm(false);
              setEditingGig(null);
              setInitialGigDate(null);
            }}
          />
        </Modal>

        {/* Modal for Expense Form */}
        <Modal
          isOpen={showExpenseForm}
          onClose={() => {
            setShowExpenseForm(false);
            setEditingExpense(null);
          }}
          title={editingExpense ? 'Edit Expense' : 'Add Expense'}
        >
          {selectedGig && (
            <ExpenseForm
              gigId={selectedGig.id}
              gig={selectedGig}
              expense={editingExpense}
              onSave={(expenseData) => {
                handleExpenseSave(expenseData);
                setShowExpenseForm(false);
                setEditingExpense(null);
              }}
              onCancel={() => {
                setShowExpenseForm(false);
                setEditingExpense(null);
              }}
            />
          )}
        </Modal>

        {/* Modal for Additional Charge Form */}
        <Modal
          isOpen={showAdditionalChargeForm}
          onClose={() => {
            setShowAdditionalChargeForm(false);
            setEditingAdditionalCharge(null);
          }}
          title={editingAdditionalCharge ? 'Edit Additional Charge' : 'Add Additional Charge'}
        >
          {selectedGig && (
            <AdditionalChargeForm
              gigId={selectedGig.id}
              gig={selectedGig}
              charge={editingAdditionalCharge}
              onSave={handleAdditionalChargeSave}
              onCancel={() => {
                setShowAdditionalChargeForm(false);
                setEditingAdditionalCharge(null);
              }}
            />
          )}
        </Modal>
          </>
          )}
          </main>
        </div>
      </div>
      <footer className="app-footer">
        {footerText && (
          <div className="footer-content-wrapper">
            <div className="footer-content">
              {footerText}
            </div>
          </div>
        )}
        <div className="footer-bottom">
          <div className="footer-left">
            <div className="server-status">
              <span className={`status-indicator ${serverStatus}`}></span>
              <span className="status-text">
                {serverStatus === 'online' ? 'Server Online' : serverStatus === 'offline' ? 'Server Offline' : 'Checking...'}
              </span>
            </div>
          </div>
          <div className="footer-center">
            <span className="footer-brand">Esfincter</span>
          </div>
          <div className="footer-right">
            <div className="footer-version">
              Version {appVersion}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;

